import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n";
import api from "../lib/api";
import { fileToBase64 } from "../lib/format";
import { SectionTitle, Pill, PrimaryButton, WhyBanner } from "../components/common";
import { ChevronLeft, Camera, Sparkles, Loader2, Check, Lock } from "lucide-react";

const DEFAULT_ROOMS = ["Living room", "Kitchen", "Bathroom", "Bedroom"];

export default function ConditionReport() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const agreementId = sp.get("agreement");
  const [kind, setKind] = useState("movein");
  const [reports, setReports] = useState([]);
  const [rooms, setRooms] = useState(DEFAULT_ROOMS.map((n) => ({ name: n, condition: "good", notes: "", photos: [] })));
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);

  const load = useCallback(() => api.get("/condition-reports", { params: agreementId ? { agreement_id: agreementId } : {} }).then((r) => setReports(r.data)), [agreementId]);
  useEffect(() => { load(); }, [load]);

  const setRoom = (i, k, v) => setRooms((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const addPhoto = async (i, e) => { const b64 = await Promise.all(Array.from(e.target.files).slice(0, 2).map(fileToBase64)); setRoom(i, "photos", [...(rooms[i].photos || []), ...b64].slice(0, 2)); };

  const save = async () => {
    setBusy(true);
    try { await api.post("/condition-reports", { agreement_id: agreementId, kind, rooms }); await load(); alert("Report saved. Both sides confirm to lock it."); }
    catch { alert("Failed"); } finally { setBusy(false); }
  };
  const confirm = async (id) => { await api.post(`/condition-reports/${id}/confirm`); await load(); };

  const runCompare = async () => {
    const movein = reports.find((r) => r.kind === "movein");
    const moveout = reports.find((r) => r.kind === "moveout");
    if (!movein || !moveout) { alert("Need both move-in and move-out reports."); return; }
    setCmpBusy(true); setCompare("");
    try {
      const mi = movein.rooms.flatMap((r) => r.photos || []);
      const mo = moveout.rooms.flatMap((r) => r.photos || []);
      const { data } = await api.post("/ai/compare-condition", { movein_photos: mi, moveout_photos: mo, language: lang || "en", room: "whole home" });
      setCompare(data.result);
    } catch { setCompare("Could not compare. Ensure photos exist in both reports."); } finally { setCmpBusy(false); }
  };

  return (
    <div className="max-w-md md:max-w-4xl mx-auto min-h-screen bg-sand pb-28">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="cr-back"><ChevronLeft className="w-6 h-6 text-slate-600" /></button>
        <div className="font-head font-bold text-slate-900">{t("condition_report")}</div>
      </header>

      <div className="px-4 py-4 space-y-4">
        <div className="flex rounded-xl bg-slate-100 p-1 text-sm font-semibold">
          <button onClick={() => setKind("movein")} data-testid="cr-movein-tab" className={`flex-1 py-2 rounded-lg ${kind === "movein" ? "bg-white shadow text-terracotta" : "text-slate-500"}`}>{t("movein")}</button>
          <button onClick={() => setKind("moveout")} data-testid="cr-moveout-tab" className={`flex-1 py-2 rounded-lg ${kind === "moveout" ? "bg-white shadow text-terracotta" : "text-slate-500"}`}>{t("moveout")}</button>
        </div>

        <WhyBanner>Photos are timestamped and locked once both sides confirm. This is your proof at move-out.</WhyBanner>

        <div className="space-y-3">
          {rooms.map((room, i) => (
            <div key={room.name} className="gs-card p-4" data-testid={`cr-room-${i}`}>
              <div className="font-head font-semibold text-slate-800 mb-2">{room.name}</div>
              <div className="flex gap-2 mb-2">
                {["good", "fair", "damaged"].map((c) => <button key={c} onClick={() => setRoom(i, "condition", c)} data-testid={`cr-${i}-${c}`} className={`flex-1 gs-tap rounded-lg border text-xs font-semibold capitalize ${room.condition === c ? { good: "bg-trust text-white border-trust", fair: "bg-amberw text-white border-amberw", damaged: "bg-red-500 text-white border-red-500" }[c] : "border-slate-200 text-slate-500"}`}>{c}</button>)}
              </div>
              <label data-testid={`cr-photo-${i}`} className="border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center gap-2 py-3 cursor-pointer text-slate-400 text-sm"><Camera className="w-4 h-4" /> {t("add_photos")}<input type="file" accept="image/*" multiple hidden onChange={(e) => addPhoto(i, e)} /></label>
              {room.photos?.length > 0 && <div className="flex gap-2 mt-2">{room.photos.map((p) => <img key={p.slice(0, 48)} src={p} alt="" className="w-16 h-16 rounded-lg object-cover" />)}</div>}
            </div>
          ))}
        </div>

        <PrimaryButton onClick={save} disabled={busy} data-testid="cr-save-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `${t("save")} ${kind === "movein" ? t("movein") : t("moveout")}`}</PrimaryButton>

        {reports.length > 0 && (
          <div>
            <SectionTitle>Saved reports</SectionTitle>
            <div className="space-y-2">
              {reports.map((r) => (
                <div key={r.id} className="gs-card p-3.5 flex items-center justify-between" data-testid={`cr-report-${r.id}`}>
                  <div><div className="font-semibold text-slate-800 capitalize">{r.kind === "movein" ? t("movein") : t("moveout")}</div><div className="text-xs text-slate-400">{r.rooms?.length} rooms</div></div>
                  {r.locked ? <Pill tone="trust"><Lock className="w-3 h-3" /> Locked</Pill> : <button onClick={() => confirm(r.id)} data-testid={`cr-confirm-${r.id}`} className="text-sm font-semibold text-terracotta">{t("confirm")}</button>}
                </div>
              ))}
            </div>
            <button onClick={runCompare} disabled={cmpBusy} data-testid="cr-compare-btn" className="gs-tap w-full mt-3 rounded-xl bg-slate2 text-white font-semibold flex items-center justify-center gap-2">{cmpBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} {t("compare_ai")}</button>
            {compare && <div className="gs-card p-4 mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="cr-compare-result"><div className="flex items-center gap-1.5 text-terracotta font-semibold text-xs mb-2"><Sparkles className="w-3.5 h-3.5" /> Sathi AI</div>{compare}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
